import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AUTO_REGION, put, type Ingest } from '../api';
import { Icon } from '../ui/Icon';
import { Note } from '../ui/tracks';
import { t } from '../i18n';
import { tn } from '../admin/network/nodeSwitch';

/**
 * Ingest region (docs/m2-edge-node.md §5): Auto or one of the regions with
 * online nodes. The choice is saved with PUT /api/me/ingest-region, then
 * /api/ingest is read again: the server and Relay Link addresses come from
 * there already for the chosen region, the UI never builds them.
 * The selected option shows the server's value, not the click.
 * Hidden when there are no regions to choose from.
 * A node group (GROUP-2c) is one tile named after the group (us-east-1). The tiles say nothing
 * about how many servers stand behind a region or whether it takes SRT (owner, NG-04): the
 * protocols below say what works.
 * GROUP-UI-3 (NG-20 A): with a default group (GROUP-DEFAULT, owner V1) the Auto tile names it
 * in mono under the word; a chosen region that is gone (`region_replaced`) gets one plain line: it went into
 * a group (the OBS address keeps working: node names stay) or to the automatic region.
 */
export function IngestRegionPicker({ ingest, compact }: { ingest?: Ingest; compact?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const regions = ingest?.regions ?? [];
  if (!ingest || regions.length === 0) return null;

  const chosen = ingest.region ?? AUTO_REGION;
  const inUse = ingest.recommended_region || AUTO_REGION;
  // A chosen region whose nodes all went away: the addresses are the automatic ones.
  const gone = ingest.region !== null && !regions.some((r) => r.region === ingest.region) && !ingest.region_replaced;
  const options = [
    { id: AUTO_REGION, label: t('ingest.region.auto'), sub: ingest.auto_group ? <span className="font-mono" data-testid="ingest-region-auto-group">{ingest.auto_group}</span> : t('ingest.region.autoSub') },
    ...regions.map((r) => ({ id: r.region, label: r.region, sub: '\u00a0' as ReactNode })),
    ...(gone ? [{ id: ingest.region!, label: ingest.region!, sub: t('ingest.region.gone') }] : []),
  ];

  const choose = async (id: string) => {
    if (busy || id === chosen) return;
    setBusy(id);
    setErr('');
    try {
      await put<Ingest>('/api/me/ingest-region', { region: id === AUTO_REGION ? null : id });
      await qc.refetchQueries({ queryKey: ['ingest'], exact: true });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="ingest-region" data-region={chosen} data-in-use={inUse} className="flex flex-col gap-1.5">
      <span id="ingest-region-label" className="text-xs text-muted">{t('ingest.region.label')}</span>
      <div role="radiogroup" aria-labelledby="ingest-region-label" aria-busy={!!busy} className="grid grid-cols-[repeat(auto-fill,minmax(118px,1fr))] gap-1.5">
        {options.map((o) => {
          const sel = o.id === chosen;
          const used = o.id === inUse;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={sel}
              disabled={!!busy}
              data-testid={`ingest-region-${o.id}`}
              data-in-use={used ? 'true' : undefined}
              title={used ? t('ingest.region.inUseHint') : undefined}
              onClick={() => void choose(o.id)}
              className={`flex min-h-[44px] min-w-0 flex-col justify-center rounded-md border px-2.5 py-1 text-left leading-tight disabled:cursor-wait ${compact ? 'md:min-h-[34px]' : 'md:min-h-[38px]'} ${
                sel ? 'border-accent/60 bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'border-line-2 text-text-soft hover:text-text'
              }`}
            >
              {/* Fixed rows in a tile of fixed width (the grid's): the spinner and
                  IN USE take a line of their own, reserved in every tile, so a
                  switch never changes a tile's size or the panel below. */}
              <span className="truncate font-mono text-xs font-medium">{o.label}</span>
              <span className="truncate text-[11px] text-muted">{o.sub}</span>
              <span className="flex h-3.5 items-center gap-1 font-mono text-[10px] font-semibold tracking-[.08em]">
                {busy === o.id ? (
                  <span aria-hidden className="sx-spin !inline-block !h-2.5 !w-2.5" />
                ) : used ? (
                  <span className="text-text-soft">{t('ingest.region.inUse')}</span>
                ) : null}
              </span>
</button>
          );
        })}
      </div>
      {gone ? (
        <div data-testid="ingest-region-warning" role="status" className="flex items-start gap-1 text-[11px] text-warn">
          <Icon name="state-warn" size={12} className="mt-px" />{t('ingest.region.goneWarn', { region: ingest.region! })}
        </div>
      ) : (
        <div className="text-[11px] text-muted">{t('ingest.region.help')}</div>
      )}
      {ingest.region_replaced && (
        <Note data-testid="ingest-region-moved">
          {ingest.region_replaced.to === AUTO_REGION
            ? tn('ingest.region.movedAuto', { from: ingest.region_replaced.from })
            : tn('ingest.region.moved', { from: ingest.region_replaced.from, to: ingest.region_replaced.to })}
        </Note>
      )}
      {err && <div role="alert" data-testid="ingest-region-error" className="flex items-center gap-1 text-xs text-fail"><Icon name="state-fail" size={12} />{t('ingest.region.error', { why: err })}</div>}
    </div>
  );
}
